
/**
 * BookingModal - API-based booking flow component
 *
 * One of two booking flows available to admin users (the other is WhitelabelIframeModal).
 * Users choose between flows via BookingMethodSelectionModal.
 *
 * Flow:
 * 1. Rate Selection - HotelDetailView shows immediately with loading spinner while hotel lookup runs
 * 2. Checkout - CombinedGuestPaymentForm for guest info and payment
 * 3. Error - Shown only if hotel lookup completely fails (rare)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import type { Hotel, HotelRate, HotelRoom, HotelIdLookupResponse, HotelSearchPricing } from '@/services/nuiteeBookingService';
import { nuiteeBookingService } from '@/services/nuiteeBookingService';
import { log, debug, error as logError } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_BOOKING_MODAL_OPENED,
  EVENT_BOOKING_MODAL_CLOSED,
  EVENT_BOOKING_ROOM_SELECTED,
  EVENT_BOOKING_SEARCH_UPDATED,
  EVENT_BOOKING_NEARBY_HOTELS_CLICKED,
  EVENT_BOOKING_SSO_TAB_OPENED,
  EVENT_BOOKING_SSO_POPUP_BLOCKED,
  EVENT_BOOKING_SSO_API_ERROR,
} from '@/lib/analyticsEvents';
import { openNuiteeSsoInNewTab } from '@/lib/booking/openNuiteeSsoInNewTab';
import BookingSsoBlockedModal from './BookingSsoBlockedModal';
import { formatDateToYYYYMMDD } from '@/utils/dateUtils';
import ModalOverlay from '../ModalOverlay/ModalOverlay';
import HotelDetailView from './HotelDetailView';
import CombinedGuestPaymentForm from './CombinedGuestPaymentForm';
import Button from '../Buttons/Button/Button';
import './BookingModal.css';
import '../ModalOverlay/ModalOverlay.css';

export type BookingStep = 'rate' | 'checkout' | 'error';

/** Default check-in (30 days from today) and check-out (+2 nights) as YYYY-MM-DD when no initialSearchParams. */
function getDefaultDates(): { checkIn: string; checkOut: string } {
  const now = new Date();
  const checkIn = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30);
  const checkOut = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate() + 2);
  return {
    checkIn: formatDateToYYYYMMDD(checkIn),
    checkOut: formatDateToYYYYMMDD(checkOut),
  };
}

// State for exact hotel matching lookup
interface HotelLookupState {
  isLoading: boolean;
  exactMatch: boolean;
  hotelId: string | null;
  hotelName: string | null;
  hotelData: HotelIdLookupResponse['data'] | null;
  error: string | null;
}

export interface BookingContext {
  placeId?: string; // Google Place ID
  latitude?: number;
  longitude?: number;
  postId?: string;
  hotelId?: string; // Nuitee hotel ID (optional, may not be known until prebook)
  locationId?: string; // Canonical locations._id for creator booking attribution
  postLocationId?: string; // PostLocation slot id when booking from a trip/list place
}

// Initial search params from place modal (dates/guests selected inline)
export interface InitialSearchParams {
  checkIn: string;
  checkOut: string;
  adults: number;
  rooms: number;
}

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: BookingContext;
  initialSearchParams?: InitialSearchParams; // Pre-filled dates/guests from place modal
}

interface BookingState {
  checkIn: string | null;
  checkOut: string | null;
  adults: number;
  rooms: number;
  selectedHotel: Hotel | null;
  selectedRate: HotelRate | null;
  prebookId: string | null;
  transactionId: string | null;
  secretKey: string | null;
  sandbox: boolean | undefined; // From prebook response - use to determine Payment SDK publicKey
  holder: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  guests: Array<{ occupancyNumber: number; firstName: string; lastName: string; email: string }> | null;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, context, initialSearchParams }) => {
  const hasInitialSearchParams = !!(initialSearchParams?.checkIn && initialSearchParams?.checkOut);
  const [_hasProcessedInitialParams, setHasProcessedInitialParams] = useState(false);

  useEffect(() => {
    if (isOpen) {
      log('[BookingModal] Modal opened with context', {
        hasContext: !!context,
        contextObject: context,
        contextKeys: context ? Object.keys(context) : [],
        postId: context?.postId,
        hasInitialSearchParams,
        initialSearchParams,
      });
      WebAnalytics.track(EVENT_BOOKING_MODAL_OPENED, {
        hotel_id: context?.hotelId || null,
        post_id: context?.postId || null,
        place_id: context?.placeId || null,
        check_in: initialSearchParams?.checkIn || null,
        check_out: initialSearchParams?.checkOut || null,
        adults: initialSearchParams?.adults ?? 2,
        rooms: initialSearchParams?.rooms ?? 1,
      });
    }
  }, [isOpen, context, hasInitialSearchParams, initialSearchParams]);

  const [step, setStep] = useState<BookingStep>('rate');

  const [bookingState, setBookingState] = useState<BookingState>(() => {
    if (initialSearchParams?.checkIn && initialSearchParams?.checkOut) {
      return {
        checkIn: initialSearchParams.checkIn,
        checkOut: initialSearchParams.checkOut,
        adults: initialSearchParams.adults ?? 2,
        rooms: initialSearchParams.rooms ?? 1,
        selectedHotel: null,
        selectedRate: null,
        prebookId: null,
        transactionId: null,
        secretKey: null,
        sandbox: undefined,
        holder: null,
        guests: null,
      };
    }
    const { checkIn, checkOut } = getDefaultDates();
    return {
      checkIn,
      checkOut,
      adults: 2,
      rooms: 1,
      selectedHotel: null,
      selectedRate: null,
      prebookId: null,
      transactionId: null,
      secretKey: null,
      sandbox: undefined,
      holder: null,
      guests: null,
    };
  });

  const [isWhitelabelSearchLoading, setIsWhitelabelSearchLoading] = useState(false);
  const [whitelabelBlockedLoginUrl, setWhitelabelBlockedLoginUrl] = useState<string | undefined>(undefined);
  const whitelabelCooldownRef = useRef<NodeJS.Timeout | null>(null);

  // State for exact hotel matching lookup
  const [hotelLookup, setHotelLookup] = useState<HotelLookupState>({
    isLoading: false,
    exactMatch: false,
    hotelId: null,
    hotelName: null,
    hotelData: null,
    error: null,
  });

  useEffect(() => {
    const cooldownTimer = whitelabelCooldownRef.current;
    return () => {
      if (cooldownTimer) clearTimeout(cooldownTimer);
    };
  }, []);

  // When modal opens with a Nuitee hotelId, search directly — hotelId takes priority over placeId.
  // placeId may be a city-level scope ID (for whitelabel) rather than the hotel's own Place ID,
  // so we always prefer the direct hotelId lookup when available.
  useEffect(() => {
    if (!isOpen || !context.hotelId) {
      return;
    }

    setHotelLookup({
      isLoading: true,
      exactMatch: false,
      hotelId: null,
      hotelName: null,
      hotelData: null,
      error: null,
    });

    const checkIn = bookingState.checkIn || getDefaultDates().checkIn;
    const checkOut = bookingState.checkOut || getDefaultDates().checkOut;

    log('[BookingModal] Searching by hotelId (Nuitee-only)', { hotelId: context.hotelId });

    nuiteeBookingService
      .searchHotels({
        hotelIds: [context.hotelId],
        checkIn,
        checkOut,
        adults: bookingState.adults,
        rooms: bookingState.rooms,
      })
      .then((searchResponse) => {
        const hotelsList = searchResponse.data?.hotels || [];
        if (searchResponse.pricing?.margin_applied && searchResponse.pricing?.message) {
          setPricingInfo(searchResponse.pricing);
        }
        setHotelLookup({
          isLoading: false,
          exactMatch: true,
          hotelId: context.hotelId ?? null,
          hotelName: hotelsList[0]?.name ?? null,
          hotelData: null,
          error: null,
        });
        setHasProcessedInitialParams(true);
        if (hotelsList.length > 0) {
          setBookingState((prev) => ({ ...prev, selectedHotel: hotelsList[0] }));
        } else {
          const minimalHotel: Hotel = {
            hotelId: context.hotelId ?? '',
            name: 'Hotel',
            address: '',
            city: '',
            country: '',
            rooms: [],
            rates: [],
          };
          setBookingState((prev) => ({ ...prev, selectedHotel: minimalHotel }));
        }
      })
      .catch((err) => {
        logError('[BookingModal] Failed to search by hotelId', err);
        setHotelLookup({
          isLoading: false,
          exactMatch: false,
          hotelId: null,
          hotelName: null,
          hotelData: null,
          error: err?.message ?? 'Failed to load hotel',
        });
        setHasProcessedInitialParams(true);
        setStep('error');
      });
    // bookingState values (adults, checkIn, checkOut, rooms) are intentionally read as initial values when the modal opens;
    // adding them would re-trigger the hotel search on every guest/date change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, context.hotelId]);

  // Look up hotel ID when modal opens via placeId — only when no direct Nuitee hotelId is available.
  // When hotelId is present, Effect 1 handles the booking; placeId is reserved for whitelabel scoping.
  useEffect(() => {
    if (!isOpen || !context.placeId || context.hotelId) {
      return;
    }

    // Reset lookup state when modal opens
    setHotelLookup({
      isLoading: true,
      exactMatch: false,
      hotelId: null,
      hotelName: null,
      hotelData: null,
      error: null,
    });

    log('[BookingModal] Looking up hotel ID for Place ID', { placeId: context.placeId });

    nuiteeBookingService.getHotelIdFromPlaceId(context.placeId)
      .then((response) => {
        const { data } = response;
        log('[BookingModal] Hotel ID lookup result', {
          exactMatch: data.exactMatch,
          hotelId: data.hotelId,
          hotelName: data.hotelName,
          error: data.error,
        });

        setHotelLookup({
          isLoading: false,
          exactMatch: data.exactMatch,
          hotelId: data.hotelId,
          hotelName: data.hotelName,
          hotelData: data,
          error: data.error || null,
        });
        setHasProcessedInitialParams(true);

        // When exact match is found, search that hotel and go to rate selection
        if (data.exactMatch && data.hotelId) {
          const checkIn = bookingState.checkIn;
          const checkOut = bookingState.checkOut;
          const adults = bookingState.adults;
          const rooms = bookingState.rooms;
          if (checkIn && checkOut) {
            nuiteeBookingService.searchHotels({
              hotelIds: [data.hotelId],
              checkIn,
              checkOut,
              adults,
              rooms,
            })
              .then((searchResponse) => {
                const hotelsList = searchResponse.data?.hotels || [];
                if (searchResponse.pricing?.margin_applied && searchResponse.pricing?.message) {
                  setPricingInfo(searchResponse.pricing);
                }
                if (hotelsList.length > 0) {
                  setBookingState((prev) => ({ ...prev, selectedHotel: hotelsList[0] }));
                  log('[BookingModal] Hotel search completed with rooms', {
                    hotelName: hotelsList[0].name,
                    ratesCount: hotelsList[0].rates?.length || 0,
                  });
                } else {
                  log('[BookingModal] Hotel search completed with no rooms for these dates');
                  // Create minimal hotel object from lookup data for display
                  const minimalHotel: Hotel = {
                    hotelId: data.hotelId || '',
                    name: data.hotelName || 'Hotel',
                    address: data.address || '',
                    city: '',
                    country: '',
                    rooms: [],
                    rates: [],
                  };
                  setBookingState((prev) => ({ ...prev, selectedHotel: minimalHotel }));
                }
              })
              .catch((err) => {
                logError('[BookingModal] Failed to search exact match hotel', err);
                // Still try to create minimal hotel for display
                const minimalHotel: Hotel = {
                  hotelId: data.hotelId || '',
                  name: data.hotelName || 'Hotel',
                  address: data.address || '',
                  city: '',
                  country: '',
                  rooms: [],
                  rates: [],
                };
                setBookingState((prev) => ({ ...prev, selectedHotel: minimalHotel }));
              });
            return;
          }
        }
        setStep('error');
      })
      .catch((err: unknown) => {
        const errObj = err as { code?: string; status?: number; response?: { data?: { code?: string; error?: string }; status?: number }; message?: string };
        const errorCode = errObj?.code || errObj?.response?.data?.code;
        const status = errObj?.status || errObj?.response?.status;
        let errorMessage = 'Failed to look up hotel';

        // Provide user-friendly error messages based on error code
        if (errorCode === 'MISSING_PLACE_ID' || errorCode === 'INVALID_PLACE_ID') {
          errorMessage = 'Invalid location. Please try again.';
        } else if (errorCode === 'RATE_LIMIT_EXCEEDED' || status === 429) {
          errorMessage = 'Too many requests. Please try again in a moment.';
        } else if (errorCode === 'SERVICE_UNAVAILABLE' || status === 503) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (errorCode === 'TIMEOUT' || (status ?? 0) === 504) {
          errorMessage = 'Request timed out. Please try again.';
        } else if (errorCode === 'INVALID_REQUEST' || ((status ?? 0) >= 400 && (status ?? 0) < 500)) {
          errorMessage = 'Invalid request. Please try again.';
        } else if (status === 500 || errorCode === 'LOOKUP_FAILED') {
          errorMessage = 'Unable to look up hotel. We\'ll search nearby hotels instead.';
        } else {
          // For unknown errors, use a generic message and fall back to proximity search
          errorMessage = errObj?.response?.data?.error || errObj?.message || errorMessage;
        }

        logError('[BookingModal] Hotel ID lookup failed', {
          errorCode,
          status,
          message: errorMessage,
          error: err,
        });

        // On error, set lookup state to indicate no exact match (will fall back to proximity search)
        setHotelLookup({
          isLoading: false,
          exactMatch: false,
          hotelId: null,
          hotelName: null,
          hotelData: null,
          error: errorMessage,
        });
        setHasProcessedInitialParams(true);
        setStep('error');
      });
    // bookingState values and context.hotelId are intentionally read as initial values when the modal opens;
    // adding them would re-trigger the hotel lookup on every guest/date change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, context.placeId]);

  // When modal opens without hotelId or placeId (only lat/lng), go straight to error
  useEffect(() => {
    if (!isOpen || context.hotelId || context.placeId) {
      return;
    }
    log('[BookingModal] No hotelId or placeId available, showing error state');
    setHotelLookup({
      isLoading: false,
      exactMatch: false,
      hotelId: null,
      hotelName: null,
      hotelData: null,
      error: 'No hotel identifier available',
    });
    setHasProcessedInitialParams(true);
    setStep('error');
  }, [isOpen, context.hotelId, context.placeId]);

  const handleRateSelected = useCallback((room: HotelRoom, rate: HotelRate) => {
    // Use the rate's offerId for prebook: each rate (e.g. refundable vs non-refundable) can
    // come from a different API roomType with its own offerId. If we used room.offerId we
    // would send the first roomType's offer and prebook could return the wrong policy/price.
    const offerIdForPrebook = rate.offerId ?? room.offerId;
    log('[BookingModal] Rate selected', {
      offerIdForPrebook,
      roomOfferId: room.offerId,
      rateId: rate.rateId,
      roomName: room.roomName,
      boardName: rate.boardName,
      price: rate.price,
      isRefundable: rate.isRefundable,
    });
    setBookingState((prev) => ({
      ...prev,
      selectedRate: {
        ...rate,
        offerId: offerIdForPrebook,
        roomName: room.roomName,
      },
    }));
    WebAnalytics.track(EVENT_BOOKING_ROOM_SELECTED, {
      hotel_id: bookingState.selectedHotel?.hotelId || null,
      hotel_name: bookingState.selectedHotel?.name || null,
      room_name: room.roomName,
      rate_id: rate.rateId,
      board_type: rate.boardName,
      price: rate.price,
      currency: rate.currency,
      check_in: bookingState.checkIn,
      check_out: bookingState.checkOut,
      adults: bookingState.adults,
      rooms: bookingState.rooms,
      post_id: context?.postId || null,
    });
    setStep('checkout');
  }, [bookingState, context]);

  // State for search change loading
  const [isSearchChanging, setIsSearchChanging] = useState(false);
  const [searchChangeError, setSearchChangeError] = useState<string | null>(null);

  // State for special pricing banner (GT+ / custom margin)
  const [pricingInfo, setPricingInfo] = useState<HotelSearchPricing | null>(null);

  // Handler for "Change search" from HotelDetailView
  const handleSearchChange = useCallback(async (
    newCheckIn: string,
    newCheckOut: string,
    newAdults: number,
    newRooms: number
  ) => {
    if (!bookingState.selectedHotel) {
      logError('[BookingModal] handleSearchChange called without selectedHotel');
      return;
    }

    log('[BookingModal] Search change requested', {
      hotelId: bookingState.selectedHotel.hotelId,
      currentDates: { checkIn: bookingState.checkIn, checkOut: bookingState.checkOut },
      newDates: { checkIn: newCheckIn, checkOut: newCheckOut },
      currentGuests: { adults: bookingState.adults, rooms: bookingState.rooms },
      newGuests: { adults: newAdults, rooms: newRooms },
    });

    setIsSearchChanging(true);
    setSearchChangeError(null);

    try {
      const response = await nuiteeBookingService.searchHotels({
        hotelIds: [bookingState.selectedHotel.hotelId],
        checkIn: newCheckIn,
        checkOut: newCheckOut,
        adults: newAdults,
        rooms: newRooms,
      });

      const hotelsList = response.data?.hotels || [];
      if (response.pricing?.margin_applied && response.pricing?.message) {
        setPricingInfo(response.pricing);
      }
      
      if (hotelsList.length > 0) {
        const updatedHotel = hotelsList[0];
        log('[BookingModal] Search change successful, updating hotel with new rates', {
          hotelId: updatedHotel.hotelId,
          name: updatedHotel.name,
          ratesCount: updatedHotel.rates?.length || 0,
        });

        setBookingState((prev) => ({
          ...prev,
          checkIn: newCheckIn,
          checkOut: newCheckOut,
          adults: newAdults,
          rooms: newRooms,
          selectedHotel: updatedHotel,
          selectedRate: null, // Reset rate selection since rates may have changed
        }));
        WebAnalytics.track(EVENT_BOOKING_SEARCH_UPDATED, {
          hotel_id: updatedHotel.hotelId,
          hotel_name: updatedHotel.name,
          check_in: newCheckIn,
          check_out: newCheckOut,
          adults: newAdults,
          rooms: newRooms,
        });
      } else {
        log('[BookingModal] No rates available for new search params');
        setSearchChangeError('No rooms available for the selected dates. Please try different dates.');
      }
    } catch (err: unknown) {
      const errObj = err as { code?: string; status?: number; response?: { data?: { code?: string }; status?: number } };
      const errorCode = errObj?.code || errObj?.response?.data?.code;
      const status = errObj?.status || errObj?.response?.status;
      let errorMessage = 'Failed to update search. Please try again.';

      if (errorCode === 'RATE_LIMIT_EXCEEDED' || status === 429) {
        errorMessage = 'Too many requests. Please try again in a moment.';
      } else if (errorCode === 'SERVICE_UNAVAILABLE' || status === 503) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (errorCode === 'TIMEOUT' || status === 504) {
        errorMessage = 'Request timed out. Please try again.';
      }

      logError('[BookingModal] Search change failed', {
        errorCode,
        status,
        message: errorMessage,
        error: err,
      });

      setSearchChangeError(errorMessage);
    } finally {
      setIsSearchChanging(false);
    }
  }, [bookingState.selectedHotel, bookingState.checkIn, bookingState.checkOut, bookingState.adults, bookingState.rooms]);

  const handleClose = useCallback(() => {
    log('[BookingModal] Closing modal');
    WebAnalytics.track(EVENT_BOOKING_MODAL_CLOSED, {
      step_at_close: step,
      hotel_id: bookingState.selectedHotel?.hotelId || null,
      hotel_name: bookingState.selectedHotel?.name || null,
      post_id: context?.postId || null,
    });
    const defaults = getDefaultDates();
    setStep('rate');
    setBookingState({
      checkIn: defaults.checkIn,
      checkOut: defaults.checkOut,
      adults: 2,
      rooms: 1,
      selectedHotel: null,
      selectedRate: null,
      prebookId: null,
      transactionId: null,
      secretKey: null,
      sandbox: undefined,
      holder: null,
      guests: null,
    });
    // Reset hotel lookup state
    setHotelLookup({
      isLoading: false,
      exactMatch: false,
      hotelId: null,
      hotelName: null,
      hotelData: null,
      error: null,
    });
    setHasProcessedInitialParams(false);
    setPricingInfo(null);
    onClose();
  }, [onClose, step, bookingState.selectedHotel, context]);

  // Opens the whitelabel hotel listing page in a new tab so users can find other hotels nearby.
  // Always targets the listing page (/hotels), never a specific hotel detail page.
  // When a placeId is available the listing is pre-scoped to that location; otherwise
  // the listing opens with dates/guests only and the user can choose a destination.
  // Uses the tab-first pattern to avoid popup-blocker silent failures.
  const handleOpenWhitelabelSearch = useCallback(() => {
    setIsWhitelabelSearchLoading(true);
    setWhitelabelBlockedLoginUrl(undefined);

    log('[BookingModal] Whitelabel search triggered', {
      hasSelectedHotel: !!bookingState.selectedHotel,
      hasPlaceId: !!context.placeId,
      hasPostId: !!context.postId,
    });

    // Capture current state for the async fetchLoginUrl closure.
    const capturedState = bookingState;
    const capturedContext = context;

    openNuiteeSsoInNewTab({
      surface: 'booking_modal',
      fetchLoginUrl: async () => {
        debug('[BookingModal] Building whitelabel listing deep link', {
          contextPlaceId: capturedContext.placeId || null,
          selectedHotelPlaceId: capturedState.selectedHotel?.google_place_id || null,
          checkIn: capturedState.checkIn,
          checkOut: capturedState.checkOut,
          adults: capturedState.adults,
          rooms: capturedState.rooms,
          postId: capturedContext.postId || null,
        });

        // Resolve placeId with fallback priority:
        //   1. context.placeId (from google_place_id in locationData, set by backend enrichment)
        //   2. selectedHotel.google_place_id (returned by Nuitee search, covers hotelId-only path)
        const resolvedPlaceId = capturedContext.placeId || capturedState.selectedHotel?.google_place_id || undefined;

        const deepLinkPath = nuiteeBookingService.buildWhitelabelListingDeepLinkPath({
          placeId: resolvedPlaceId,
          checkIn: capturedState.checkIn ?? '',
          checkOut: capturedState.checkOut ?? '',
          adults: capturedState.adults,
          rooms: capturedState.rooms,
          postId: capturedContext.postId,
        });

        log('[BookingModal] Requesting SSO link', { deepLinkPath });
        const ssoResponse = await nuiteeBookingService.getSSOLink(deepLinkPath);

        if (!ssoResponse.data?.loginUrl) {
          log('[BookingModal] SSO response missing loginUrl', { ssoResponse });
          throw new Error('Missing loginUrl in SSO response');
        }

        log('[BookingModal] SSO link obtained', {
          redirectUrl: ssoResponse.data.redirectUrl,
          clientRef: ssoResponse.data.redirectUrl?.match(/clientReference=([^&]*)/)?.[1] || 'not-found',
        });

        return ssoResponse.data.loginUrl;
      },
      onSuccess: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_TAB_OPENED, {
          surface: 'booking_modal',
          loginUrl: !!loginUrl,
          ...WebAnalytics.getReferringCreatorProps(),
        });
        WebAnalytics.track(EVENT_BOOKING_NEARBY_HOTELS_CLICKED, {
          hotel_id: capturedState.selectedHotel?.hotelId || null,
          hotel_name: capturedState.selectedHotel?.name || null,
          post_id: capturedContext?.postId || null,
          check_in: capturedState.checkIn,
          check_out: capturedState.checkOut,
        });
        setIsWhitelabelSearchLoading(false);
        // Close the modal now that the new tab has opened
        handleClose();
      },
      onBlocked: (loginUrl) => {
        WebAnalytics.track(EVENT_BOOKING_SSO_POPUP_BLOCKED, { surface: 'booking_modal', hasLoginUrl: !!loginUrl });
        setWhitelabelBlockedLoginUrl(loginUrl);
        setIsWhitelabelSearchLoading(false);
      },
      onApiError: (errorCode, errorStatus) => {
        logError('[BookingModal] Error opening whitelabel search', { errorCode, errorStatus });
        WebAnalytics.track(EVENT_BOOKING_SSO_API_ERROR, { surface: 'booking_modal', errorCode, errorStatus });

        if (errorCode === 'HOTEL_NOT_IN_INVENTORY') {
          toast.error('This hotel is not available for booking at this time.');
        } else if (errorCode === 'USER_NOT_FOUND' || errorCode === 'MISSING_USER_EMAIL') {
          toast.error('Please log in to book hotels.');
        } else if (errorStatus === 401 || errorStatus === 403) {
          toast.error('Please log in to book hotels.');
        } else if (errorStatus === 429) {
          toast.error('Too many requests. Please wait a moment and try again.');
        } else if (errorStatus === 503 || errorStatus === 504) {
          toast.error('Booking service is temporarily unavailable. Please try again later.');
        } else {
          toast.error('Unable to open search. Please try again.');
        }
        setIsWhitelabelSearchLoading(false);
      },
    });
  }, [bookingState, context, handleClose]);

  if (!isOpen && !whitelabelBlockedLoginUrl) {
    return null;
  }

  return (
    <>
    {isOpen && <ModalOverlay onClose={handleClose}>
      <div className={`booking-modal${step === 'error' ? ' booking-modal-error-size' : ''}`}>
        <div className={`booking-modal-content${step === 'checkout' ? ' booking-modal-content--checkout' : ''}`}>
          {step === 'error' && (
            <div className="booking-modal-error">
              <div className="booking-modal-error-icon">⚠️</div>
              <h3 className="booking-modal-error-title">Hotel Not Found</h3>
              <p className="booking-modal-error-message">
                We couldn&apos;t find an exact match for this hotel in our booking system.
                Please try searching for a different hotel or contact support for assistance.
              </p>
              <div className="booking-modal-error-actions">
                <Button variant="secondary" onClick={handleClose}>
                  Close
                </Button>
                <Button variant="primary" onClick={handleOpenWhitelabelSearch} disabled={isWhitelabelSearchLoading}>
                  {isWhitelabelSearchLoading ? 'Opening in new tab…' : 'Search Nearby'}
                </Button>
              </div>
              {isWhitelabelSearchLoading && (
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  If nothing opens, your browser may be blocking pop-ups.
                </p>
              )}
            </div>
          )}

          {step === 'rate' && bookingState.checkIn && bookingState.checkOut && (
            <HotelDetailView
              hotel={bookingState.selectedHotel}
              checkIn={bookingState.checkIn}
              checkOut={bookingState.checkOut}
              adults={bookingState.adults}
              rooms={bookingState.rooms}
              onRateSelected={handleRateSelected}
              onBack={handleClose}
              onClose={handleClose}
              onDatesChange={handleSearchChange}
              isSearchChanging={isSearchChanging}
              searchChangeError={searchChangeError}
              isInitialLoading={hotelLookup.isLoading}
              onWiderSearch={handleOpenWhitelabelSearch}
              isWiderSearchLoading={isWhitelabelSearchLoading}
              pricingInfo={pricingInfo}
            />
          )}

          {step === 'checkout' && bookingState.selectedHotel && bookingState.selectedRate && bookingState.checkIn && bookingState.checkOut && (
            <CombinedGuestPaymentForm
              hotel={bookingState.selectedHotel}
              selectedRate={bookingState.selectedRate}
              checkIn={bookingState.checkIn}
              checkOut={bookingState.checkOut}
              adults={bookingState.adults}
              rooms={bookingState.rooms}
              context={context}
              pricingInfo={pricingInfo}
              onPaymentComplete={() => {
                log('[BookingModal] Payment completed, redirecting to completion page');
              }}
              onBack={() => setStep('rate')}
              onClose={handleClose}
              onWiderSearch={handleOpenWhitelabelSearch}
              isWiderSearchLoading={isWhitelabelSearchLoading}
            />
          )}
        </div>
      </div>
    </ModalOverlay>}

    {/* Popup-blocked recovery modal for whitelabel SSO */}
    <BookingSsoBlockedModal
      isOpen={!!whitelabelBlockedLoginUrl}
      onClose={() => setWhitelabelBlockedLoginUrl(undefined)}
      loginUrl={whitelabelBlockedLoginUrl}
    />
    </>
  );
};

export default BookingModal;
